import { useAuth } from '@clerk/clerk-react';
import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { BuildingIcon, CheckIcon, LoaderCircleIcon, LogOutIcon, PlusIcon, SettingsIcon, UserIcon } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { useContext } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Skeleton } from './ui/skeleton';

import { api } from '@/lib/clients';
import { installationUrl } from '@/lib/installation';
import { cn } from '@/lib/utils';
import { createZeroInstance, useZero } from '@/lib/zero';
import { Route } from '@/routes/_authed';
import { installationsQuery, useCurrentOrg } from '@/services/org';

declare global {
  interface CustomJwtSessionClaims {
    login?: string;
    meta?: {
      isSuperUser?: boolean;
    };
  }
}

export const UserMenu = () => {
  return (
    <Suspense fallback={<Skeleton className="size-10 rounded-sm" />}>
      <UserMenuInner />
    </Suspense>
  );
};

const UserMenuInner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();

  const { signOut, sessionClaims } = useAuth();

  const { currentOrg, setCurrentOrg, clearCurrentOrg } = useCurrentOrg();
  const { data: installations } = useSuspenseQuery(installationsQuery());

  const navigate = useNavigate();
  if (!installations || installations.length === 0) {
    navigate({ to: '/install' });
  }

  const zero = useZero();
  const [runningBackfill] = useZeroQuery(
    zero.query.BackfillStatusTable.where(({ and, cmp }) =>
      and(cmp('installationId', currentOrg.id), cmp('status', 'running'))
    ).one()
  );

  const handleInstallRequest = () => {
    window.location.href = installationUrl();
  };

  const handleLogout = () => {
    router.invalidate();
    zero.close();
    clearCurrentOrg();
    signOut();
  };

  const resetPulls = async () => {
    if (!currentOrg) {
      return;
    }
    console.log('resetting pulls');
    try {
      await api.github.reset.$post({
        json: {
          installationId: currentOrg.id,
          login: currentOrg.account.login,
          type: currentOrg.target_type as 'Organization' | 'User',
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="size-10 flex items-center justify-center hover:bg-stone-300 rounded-md cursor-pointer">
          <Avatar className="rounded-sm size-7">
            <AvatarImage src={currentOrg.account.avatar_url} className="rounded-sm" />
            <AvatarFallback>
              <BuildingIcon size={22} className="opacity-80" />
            </AvatarFallback>
          </Avatar>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 rounded" align="start" side="bottom" sideOffset={4}>
        <DropdownMenuLabel className="text-muted-foreground text-xs">Organizations</DropdownMenuLabel>
        {!installations && (
          <DropdownMenuItem>
            <LoaderCircleIcon className="size-4" />
          </DropdownMenuItem>
        )}
        {installations &&
          installations.map((install) => (
            <DropdownMenuItem
              key={install.account.login}
              onClick={() => setCurrentOrg(install)}
              className="text-primary gap-2 p-2 hover:cursor-pointer"
            >
              <Avatar className="size-5 rounded-sm">
                <AvatarImage src={install.account.avatar_url} />
              </Avatar>
              {install.account.login}
              {install.id === currentOrg.id && <CheckIcon className="ml-auto size-4" />}
            </DropdownMenuItem>
          ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2 p-2 hover:cursor-pointer"
          onClick={() => navigate({ to: '/$owner/settings', params: { owner: currentOrg.account.login } })}
        >
          <div className="bg-muted flex size-6 items-center justify-center rounded border">
            <SettingsIcon className="size-4" />
          </div>
          <span className="text-foreground font-light">Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 p-2 hover:cursor-pointer" onClick={handleInstallRequest}>
          <div className="bg-muted flex size-6 items-center justify-center rounded border">
            <PlusIcon className="size-4" />
          </div>
          <span className="text-foreground font-light">Manage Org Permissions</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2 p-2 hover:cursor-pointer"
          onClick={resetPulls}
          disabled={runningBackfill !== undefined}
        >
          <div className="bg-muted flex size-6 items-center justify-center rounded border">
            <ArrowClockwiseIcon className={cn(runningBackfill && 'animate-spin')} />
          </div>
          <span className="text-foreground font-light">Reset Data</span>
        </DropdownMenuItem>
        {sessionClaims?.meta?.isSuperUser && (
          <DropdownMenuItem className="gap-2 p-2 hover:cursor-pointer" onClick={() => navigate({ to: '/admin' })}>
            <div className="bg-muted flex size-6 items-center justify-center rounded border">
              <UserIcon />
            </div>
            <span className="text-foreground font-light">Admin Panel</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2 p-2 hover:cursor-pointer" onClick={handleLogout}>
          <div className="bg-muted flex size-6 items-center justify-center rounded border">
            <LogOutIcon className="size-4" />
          </div>
          <span className="text-foreground font-light">Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
